import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../ui/table';
import { ChevronDownIcon } from '../../../icons';

export type ColumnDef = {
  key: string;
  label: string;
};

export type AccordionRow = {
  id: string | number;
  parent: Record<string, React.ReactNode>;
  children?: Array<Record<string, React.ReactNode>>;
};

export interface AccordianTableProps {
  parentColumns: ColumnDef[];
  childColumns: ColumnDef[];
  rows: AccordionRow[];
  title?: string;
  footer?: { label: React.ReactNode; value: React.ReactNode };
  onExpand?: (rowId: string | number) => void;
  expandedByDefault?: boolean;
}

const AccordianTable: React.FC<AccordianTableProps> = ({
  parentColumns,
  childColumns,
  rows,
  title,
  footer,
  onExpand,
  expandedByDefault = true,
}) => {
  // Initialize rows with expandedByDefault setting
  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(() => {
    const initialExpanded: Record<string | number, boolean> = {};
    rows.forEach(row => {
      initialExpanded[row.id] = expandedByDefault;
    });
    return initialExpanded;
  });

  const toggleExpand = (id: string | number) => {
    setExpanded((prev) => {
      const newExpanded = { ...prev, [id]: !prev[id] };
      // Call onExpand callback when expanding (transitioning from false to true)
      if (!prev[id] && newExpanded[id] && onExpand) {
        onExpand(id);
      }
      return newExpanded;
    });
  };

  const expandAll = () => {
    const allExpanded: Record<string | number, boolean> = {};
    rows.forEach(row => {
      allExpanded[row.id] = true;
      // Call onExpand callback for rows that weren't already expanded
      if (!expanded[row.id] && onExpand) {
        onExpand(row.id);
      }
    });
    setExpanded(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed: Record<string | number, boolean> = {};
    rows.forEach(row => {
      allCollapsed[row.id] = false;
    });
    setExpanded(allCollapsed);
  };

  // Check if all rows are expanded
  const allExpanded = rows.every(row => expanded[row.id]);

  // Update expanded state when rows change (e.g., new data loaded)
  useEffect(() => {
    setExpanded(prev => {
      const newExpanded = { ...prev };
      rows.forEach(row => {
        // Only set state for new rows — use expandedByDefault, not a hardcoded true
        if (!(row.id in newExpanded)) {
          newExpanded[row.id] = expandedByDefault;
        }
      });
      return newExpanded;
    });
  }, [rows, expandedByDefault]);

  return (
    <div className="overflow-visible rounded-xl bg-white dark:bg-white/[0.03]">
      {title && (
        <div className="px-4 py-4 border border-b-0 border-gray-100 dark:border-white/[0.05] rounded-t-xl">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>
      )}

      <div className="max-w-full overflow-x-auto custom-scrollbar">
        <Table>
          <TableHeader>
            <TableRow>
              {/* Expand/collapse control column with expand/collapse all button */}
              <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05] w-10">
                <button
                  onClick={() => {
                    if (allExpanded) {
                      collapseAll();
                    } else {
                      expandAll();
                    }
                  }}
                  className="transition-transform hover:scale-110"
                  title={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                  aria-label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                >
                  <ChevronDownIcon 
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${allExpanded ? 'rotate-180' : ''}`} 
                  />
                </button>
              </TableCell>
              {parentColumns.map((col) => (
                <TableCell key={col.key} isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-700 text-theme-xs dark:text-gray-400">{col.label}</p>
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const isOpen = Boolean(expanded[row.id]);
              return (
                <React.Fragment key={row.id}>
                  <TableRow className="hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                    <TableCell className="px-4 py-4 border border-gray-100 dark:border-white/[0.05] align-top">
                      <button
                        aria-label={isOpen ? 'Collapse row' : 'Expand row'}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(row.id);
                        }}
                        className="transition-transform hover:scale-110"
                      >
                        <ChevronDownIcon className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </TableCell>
                    {parentColumns.map((col) => (
                      <TableCell key={col.key} className="px-4 py-4 border border-gray-100 dark:border-white/[0.05] text-gray-900 dark:text-white">
                        {row.parent[col.key] ?? 'N/A'}
                      </TableCell>
                    ))}
                  </TableRow>

                  {isOpen && row.children && row.children.length > 0 && (
                    <TableRow>
                      <TableCell className="p-0 border border-gray-100 dark:border-white/[0.05]" colSpan={parentColumns.length + 1}>
                        <div className="bg-gray-50 dark:bg-gray-900/40 w-full">
                          <div className="max-w-full overflow-x-auto custom-scrollbar">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  {childColumns.map((col) => (
                                    <TableCell key={col.key} isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
                                      <p className="font-medium text-gray-700 text-theme-xs dark:text-gray-400">{col.label}</p>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {row.children.map((child, idx) => (
                                  <TableRow key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                                    {childColumns.map((col) => (
                                      <TableCell key={col.key} className="px-4 py-3 pb-4 border border-gray-100 dark:border-white/[0.05] text-gray-900 dark:text-white align-top">
                                        <div className="max-w-full overflow-visible whitespace-pre-wrap break-words">
                                          {child[col.key] ?? 'N/A'}
                                        </div>
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
            {footer && (
              <TableRow>
                <TableCell className="px-4 py-4 border border-gray-100 dark:border-white/[0.05] text-right font-semibold text-gray-900 dark:text-white" colSpan={parentColumns.length}>
                  {footer.label}
                </TableCell>
                <TableCell className="px-4 py-4 border border-gray-100 dark:border-white/[0.05] font-semibold text-gray-900 dark:text-white">
                  {footer.value}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AccordianTable;
